import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  LayoutTemplate, 
  Plus, 
  Sparkles,
  Search,
  Filter,
  Eye,
  Edit3,
  ArrowRight,
  Copy,
  Trash2,
  MoreVertical,
  Pencil,
  X,
  Loader2,
  Monitor,
  Smartphone
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api, { templatesApi, messageTypesApi, API_BASE } from '../lib/api';
import { useAuth } from '../context/AuthContext';

const categoryColors = {
  newsletter: 'bg-blue-100 text-blue-700',
  oferta: 'bg-red-100 text-red-700',
  evento: 'bg-purple-100 text-purple-700',
  anuncio: 'bg-amber-100 text-amber-700',
  bienvenida: 'bg-emerald-100 text-emerald-700',
};

const TemplateCard = ({ template, onPreview, onUse, onEdit, onDuplicate, onDelete }) => (
  <Card className="card-hover overflow-hidden group" data-testid={`template-card-${template.id}`}>
    <div className="aspect-[4/3] relative overflow-hidden bg-slate-100">
      {template.thumbnail ? (
        <img 
          src={template.thumbnail} 
          alt={template.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
          <LayoutTemplate className="w-12 h-12 text-slate-400" />
        </div>
      )}
      {template.isCustom && (
        <div className="absolute top-2 left-2">
          <Badge className="bg-indigo-600 text-white">Personalizada</Badge>
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-4 gap-2">
        <Button 
          size="sm" 
          variant="secondary" 
          onClick={() => onPreview(template)}
          data-testid={`preview-template-${template.id}`}
        >
          <Eye className="w-4 h-4 mr-1" /> Ver
        </Button>
        <Button 
          size="sm" 
          onClick={() => onUse(template)}
          data-testid={`use-template-${template.id}`}
        >
          <Edit3 className="w-4 h-4 mr-1" /> Usar
        </Button>
      </div>
    </div>
    <CardContent className="p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-slate-900 font-[Manrope]">{template.name}</h3>
          <p className="text-sm text-slate-500 mt-1 line-clamp-2">{template.description}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={categoryColors[template.category] || 'bg-slate-100 text-slate-700'}>
            {template.category}
          </Badge>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(template)}>
                <Pencil className="w-4 h-4 mr-2" />
                Editar plantilla
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onDuplicate(template)}>
                <Copy className="w-4 h-4 mr-2" />
                Duplicar
              </DropdownMenuItem>
              {template.isCustom && (
                <DropdownMenuItem 
                  onClick={() => onDelete(template)}
                  className="text-red-600"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="mt-3 flex items-center text-xs text-slate-400">
        <span>{template.sections?.length || 0} secciones</span>
      </div>
    </CardContent>
  </Card>
);

export const Templates = () => {
  const navigate = useNavigate();
  const { company } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [messageTypes, setMessageTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [deletingTemplate, setDeletingTemplate] = useState(null);
  const [duplicatingTemplate, setDuplicatingTemplate] = useState(null);
  const [duplicateName, setDuplicateName] = useState('');
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState('desktop');

  const fetchData = async () => {
    try {
      // Build params to filter templates by company sector
      const params = {};
      if (company?.sector) {
        params.filterBySector = true;
        params.sector = company.sector;
      }
      
      const [templatesRes, typesRes] = await Promise.all([
        templatesApi.list(params),
        messageTypesApi.list(),
      ]);
      setTemplates(templatesRes.data.templates);
      setMessageTypes(typesRes.data.messageTypes);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [company?.sector]);

  const filteredTemplates = templates.filter((template) => {
    const matchesSearch = template.name.toLowerCase().includes(search.toLowerCase()) ||
      template.description.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || template.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const handlePreview = async (template) => {
    setPreviewTemplate(template);
    setPreviewLoading(true);
    setPreviewHtml('');
    
    try {
      // Generate sample data for preview based on template sections
      const sectionsData = {};
      if (template.sections) {
        template.sections.forEach(section => {
          const sectionData = {};
          if (section.fields) {
            section.fields.forEach(field => {
              sectionData[field.key] = field.default || '';
            });
          }
          sectionsData[section.id] = sectionData;
        });
      }
      
      const response = await api.post('/templates/preview', {
        templateId: template.id,
        sectionsData,
        subject: template.name,
        includeCompanyData: true
      });
      
      if (response.data && response.data.html) {
        setPreviewHtml(response.data.html);
      } else {
        throw new Error('No HTML returned');
      }
    } catch (error) {
      console.error('Error loading preview:', error);
      // Fallback: try render endpoint
      try {
        const sectionsData = {};
        if (template.sections) {
          template.sections.forEach(section => {
            const sectionData = {};
            if (section.fields) {
              section.fields.forEach(field => {
                sectionData[field.key] = field.default || '';
              });
            }
            sectionsData[section.id] = sectionData;
          });
        }
        
        const response = await api.post('/templates/render', {
          templateId: template.id,
          sectionsData,
          subject: template.name,
          includeCompanyData: true
        });
        
        if (typeof response.data === 'string') {
          setPreviewHtml(response.data);
        } else if (response.data && response.data.html) {
          setPreviewHtml(response.data.html);
        }
      } catch (err) {
        toast.error('Error al cargar el preview');
        setPreviewTemplate(null);
      }
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleUse = (template) => {
    navigate(`/campaigns/new?templateId=${template.id}`);
  };

  const handleEdit = (template) => {
    navigate(`/templates/edit/${template.id}`);
  };

  const handleDuplicate = async () => {
    if (!duplicatingTemplate || !duplicateName.trim()) return;
    try {
      await api.post(`/templates/${duplicatingTemplate.id}/duplicate`, {
        newName: duplicateName.trim()
      });
      toast.success('Plantilla duplicada');
      setDuplicatingTemplate(null);
      setDuplicateName('');
      fetchData();
    } catch (error) {
      toast.error('Error al duplicar plantilla');
    }
  };

  const handleDelete = async () => {
    if (!deletingTemplate) return;
    try {
      await api.delete(`/templates/${deletingTemplate.id}`);
      toast.success('Plantilla eliminada');
      setDeletingTemplate(null);
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar plantilla');
    }
  };

  return (
    <div className="p-8 animate-fade-in" data-testid="templates-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold font-[Manrope] text-slate-900">Plantillas</h1>
          <p className="text-slate-500 mt-1">Gestiona tus plantillas de email</p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/templates/new">
            <Button variant="outline" data-testid="create-template-btn">
              <Plus className="w-4 h-4 mr-2" />
              Crear plantilla
            </Button>
          </Link>
          <Link to="/templates/generate">
            <Button className="ai-button text-white" data-testid="generate-template-btn">
              <Sparkles className="w-4 h-4 mr-2" />
              Generar con IA
            </Button>
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar plantillas..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-templates"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-48" data-testid="filter-category">
            <Filter className="w-4 h-4 mr-2 text-slate-400" />
            <SelectValue placeholder="Categoría" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las categorías</SelectItem>
            {messageTypes.map((type) => (
              <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Templates Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i} className="overflow-hidden">
              <div className="aspect-[4/3] bg-slate-100 animate-pulse" />
              <CardContent className="p-4">
                <div className="h-5 bg-slate-100 rounded animate-pulse mb-2" />
                <div className="h-4 bg-slate-100 rounded animate-pulse w-2/3" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredTemplates.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTemplates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              onPreview={handlePreview}
              onUse={handleUse}
              onEdit={handleEdit}
              onDuplicate={(t) => {
                setDuplicatingTemplate(t);
                setDuplicateName(`${t.name} (copia)`);
              }}
              onDelete={setDeletingTemplate}
            />
          ))}
        </div>
      ) : (
        <Card className="p-12 text-center">
          <LayoutTemplate className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No se encontraron plantillas</h3>
          <p className="text-slate-500 mb-4">
            {search || categoryFilter !== 'all' 
              ? 'Prueba con otros filtros de búsqueda'
              : 'Crea tu primera plantilla'}
          </p>
          <div className="flex items-center justify-center gap-3">
            <Link to="/templates/new">
              <Button variant="outline" data-testid="create-first-template">
                <Plus className="w-4 h-4 mr-2" />
                Crear plantilla
              </Button>
            </Link>
            <Link to="/templates/generate">
              <Button data-testid="generate-first-template">
                <Sparkles className="w-4 h-4 mr-2" />
                Generar con IA
              </Button>
            </Link>
          </div>
        </Card>
      )}

      {/* Duplicate Dialog */}
      <Dialog open={!!duplicatingTemplate} onOpenChange={() => setDuplicatingTemplate(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Duplicar plantilla</DialogTitle>
            <DialogDescription>
              Se creará una copia editable de "{duplicatingTemplate?.name}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="duplicate-name">Nombre de la nueva plantilla</Label>
              <Input
                id="duplicate-name"
                value={duplicateName}
                onChange={(e) => setDuplicateName(e.target.value)}
                placeholder="Nombre de la copia"
                className="mt-2"
                data-testid="duplicate-name-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDuplicatingTemplate(null)}>
              Cancelar
            </Button>
            <Button onClick={handleDuplicate} disabled={!duplicateName.trim()}>
              <Copy className="w-4 h-4 mr-2" />
              Duplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingTemplate} onOpenChange={() => setDeletingTemplate(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar plantilla?</AlertDialogTitle>
            <AlertDialogDescription>
              La plantilla "{deletingTemplate?.name}" será eliminada permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Preview Modal */}
      <Dialog open={!!previewTemplate} onOpenChange={() => setPreviewTemplate(null)}>
        <DialogContent className="max-w-4xl h-[85vh] flex flex-col p-0">
          <DialogHeader className="px-6 py-4 border-b flex-shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle>{previewTemplate?.name}</DialogTitle>
                <DialogDescription>{previewTemplate?.description}</DialogDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant={previewMode === 'desktop' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPreviewMode('desktop')}
                >
                  <Monitor className="w-4 h-4" />
                </Button>
                <Button
                  variant={previewMode === 'mobile' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPreviewMode('mobile')}
                >
                  <Smartphone className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-auto p-6 bg-slate-100">
            {previewLoading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
              </div>
            ) : previewHtml ? (
              <div className={`mx-auto bg-white shadow-lg ${previewMode === 'mobile' ? 'max-w-[375px]' : 'max-w-[650px]'}`}>
                <iframe
                  srcDoc={previewHtml}
                  title="Email Preview"
                  className="w-full border-0"
                  style={{ height: previewMode === 'mobile' ? '600px' : '800px' }}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-slate-500">
                No se pudo cargar el preview
              </div>
            )}
          </div>
          <DialogFooter className="px-6 py-4 border-t flex-shrink-0">
            <Button variant="outline" onClick={() => setPreviewTemplate(null)}>
              Cerrar
            </Button>
            <Button onClick={() => { setPreviewTemplate(null); handleUse(previewTemplate); }}>
              <Edit3 className="w-4 h-4 mr-2" />
              Usar esta plantilla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Templates;
